/*
 * Copyright (c) 2023 Pegasystems Inc.
 * All rights reserved.
 */

import { handleChange } from '../../../expression/expression-engine';
import * as actions from '../actions';
import PegaConstants from '../../../constants';
import { getValue, dispatch } from '../state-manager';
import DatapageDependencyMap from '../../../cascade/datapage-dependency-handler';
import { getPropertyMetadata, getDataPageMetadata } from '../../../rules/rules-store';
import Utils from '../../../utils/utils';
import * as RefreshHelpers from '../../../refresh/refreshHelpers';
import { getVisibleFormFields, getReadOnlyFormFields } from '../../../utils/form-handler';
import AnnotationUtils from '../../../annotation/utils';
import CascadeManager from '../../../cascade/manager';
import { isPropertyDirtyPageInstruction } from '../../../page-instructions';
import PageInstructionUtils from '../../../page-instructions/utils';
import StateMachineManager from '../../../page-instructions/state-machine-manager';
import RefreshManager from '../../../refresh/refresh-manager';
import TreeManager from '../../../contextree/treemanager';
import ActionsSequencer from '../../../actions/sequencer';
import { inspectChildrenForRefreshTargets } from '../../../refresh/refreshHelpers';
import { getTargetFromContainerItemID, getContainerItemData } from '../../../container/container-utils';

const { APP } = PegaConstants;

let changedProperties: { [key: string]: any } = {};
let additionalChangedProperties: { [key: string]: any } = {};

let isComplexObjectMergedInternal: { [key: string]: any } = {};

export const FORM_DIRTY_MESSAGE = 'You will lose unsaved changes. Do you want to continue?';
export const MAX_DOCUMENT_LIMIT_REACHED =
  'You have reached maximum limit of pending changes. Please commit/cancel one or more changes to continue.';

const mergeAdditionPropertiesChanges = (resultObj: object, context: string, visibleFields: string[]) => {
  const additionalChangedPropertiesInContext = additionalChangedProperties[context] || {};
  Object.keys(additionalChangedPropertiesInContext).forEach((key: string) => {
    const associatedProperty = additionalChangedPropertiesInContext[key].associatedProperty;
    if (
      Object.hasOwn(additionalChangedPropertiesInContext, key) &&
      (associatedProperty === undefined || visibleFields.includes(associatedProperty))
    ) {
      Utils.buildObject(key, resultObj, additionalChangedPropertiesInContext[key].value);
    }
  });

  return resultObj;
};
/**
 * Builds form submit object. Object will be
 * in json format.
 * @param context Context whose changes will be returned
 * @param includeHiddenFields check to whether add hiddenFields or not to the state
 */
export const getChanges = (context = APP.APP, includeHiddenFields?: boolean, includeReadOnlyFields?: any) => {
  let visibleFields: any[] = [];
  let readOnlyFields: any[] = [];
  if (!includeHiddenFields) {
    visibleFields = getVisibleFormFields(context, true);
  }

  if (!includeReadOnlyFields) {
    readOnlyFields = getReadOnlyFormFields(context);
  }
  const resultObj = {};
  mergeAdditionPropertiesChanges(resultObj, context, visibleFields);
  const changedPropertiesInContext = changedProperties[context] || {};
  Object.keys(changedPropertiesInContext).forEach((key) => {
    const isApplicableForHidden = (!includeHiddenFields && visibleFields.includes(key)) || includeHiddenFields;

    if (Object.hasOwn(changedPropertiesInContext, key) && isApplicableForHidden && !readOnlyFields.includes(key)) {
      Utils.buildObject(key, resultObj, changedPropertiesInContext[key].value);
    }
  });

  return resultObj;
};

export const mergeListChanges = (context: string, resultObj = {}) => {
  const pageListChanges: any = StateMachineManager.buildClientChangeList(context) || {};
  Object.keys(pageListChanges).forEach((key) => {
    Utils.buildObject(key, resultObj, pageListChanges[key]);
  });
};

/**
 * This delete a map of recorded change for a given property in respective context
 * @param payload redux action payload.
 */
const deleteRecordedChange = (payload: any) => {
  const { context } = payload;
  const reference = payload.reference.startsWith('.') ? payload.reference.substr(1) : payload.reference;
  delete changedProperties[context]?.[reference];
  delete additionalChangedProperties[context]?.[reference];
};

/* @private */
export const isComplexObjectMerged = (context: string | null | undefined) =>
  context && isComplexObjectMergedInternal[context];

const recordChangeInternal = (changedPropertiesReference: any, payload: any) => {
  const { context, initialValue = '', isStrictTracking, associatedProperty } = payload;
  const reference = payload.reference.startsWith('.') ? payload.reference.substr(1) : payload.reference;
  const changeTracker = changedPropertiesReference[reference];
  if (!changeTracker) {
    changedPropertiesReference[reference] = {
      initialValue: initialValue || getValue(payload.reference, null, context)
    };
    changedPropertiesReference[reference].previousValue = changedPropertiesReference[reference].initialValue;
  } else {
    changedPropertiesReference[reference].previousValue = getValue(payload.reference, null, context);
  }
  changedPropertiesReference[reference].value = payload.value;
  if (associatedProperty) {
    changedPropertiesReference[reference].associatedProperty = associatedProperty;
  }
  if (
    isStrictTracking &&
    changedPropertiesReference[reference].value === changedPropertiesReference[reference].initialValue
  ) {
    deleteRecordedChange(payload);
  }
};
/**
 * This creates a map for every change with initialValue and the current value
 * to be used for dirty check.
 * @param action redux action object.
 */
const recordChange = (action: any) => {
  const { payload } = action;
  const { context } = payload;
  const reference = payload.reference.startsWith('.') ? payload.reference.substr(1) : payload.reference;
  if (!TreeManager.isFieldExists(context, reference)) {
    additionalChangedProperties[context] = additionalChangedProperties[context] || {};
    recordChangeInternal(additionalChangedProperties[context], payload);
  }
  changedProperties[context] = changedProperties[context] || {};
  recordChangeInternal(changedProperties[context], payload);
};

/**
 * This api will update changedProperties map from server response when preserveClientChanges
 * is false.
 * @param context Context in which changedProperties needs update
 * @param serverResponse response from server
 */
export const updateServerChangedProperties = (context: string, serverResponse: any) => {
  const changedPropertiesInContext = changedProperties[context] || {};
  Object.keys(changedPropertiesInContext).forEach((key) => {
    if (Utils.hasIn(serverResponse, key)) {
      changedPropertiesInContext[key].value = Utils.getIn(serverResponse, key);
    }
  });
};

export const updateChangeList = ({ value, context, reference }: any) => {
  const target = getTargetFromContainerItemID(context);
  const itemData = getContainerItemData(target, context) || {};
  if (PageInstructionUtils.isPageInstruction(reference) || itemData?.subscriptFieldName) {
    return;
  }
  const referencePath = reference?.startsWith('.') ? reference?.substr(1) : reference;
  changedProperties[context] = changedProperties[context] || {};
  const changeTrackerList = changedProperties[context][referencePath];
  if (!changeTrackerList) {
    changedProperties[context][referencePath] = {
      initialValue: getValue(referencePath, null, context)
    };
  }
  changedProperties[context][referencePath].value = value;
};
/**
 * Returns true if the form in the passed context is dirty.
 * @param context Context whose dirtiness will be checked.
 */
export const isContextDirty = (context: string | null | undefined) => {
  return (
    (context &&
      changedProperties[context] &&
      Object.keys(changedProperties[context]).some((property) => {
        return changedProperties[context][property].initialValue !== changedProperties[context][property].value;
      })) ||
    !!isComplexObjectMerged(context)
  );
};

/**
 * [isPropertyDirty]
 * Description : Returns true if the property has changed after the last refresh call , else false.
 * @param context     Store context
 * @param property    Property reference
 */
const isPropertyDirty = (context: string, property: string) => {
  // If the inputs are incorrect, say property is not dirty.
  if (!(context && property)) {
    return false;
  }
  const propertyObject = changedProperties[context] && changedProperties[context][property];
  if (propertyObject) {
    return propertyObject.previousValue !== propertyObject.value;
  }
  return false;
};

/**
 * returns true if any of the forms across contexts is dirty.
 */
const isDirty = () => {
  return Object.keys(changedProperties).some((context) => {
    return isContextDirty(context);
  });
};

/**
 * Resets the changedProperties map.
 * @param context thread name
 */
export const resetChangedProperties = (context: string) => {
  if (context && changedProperties[context]) {
    changedProperties[context] = {};
    additionalChangedProperties[context] = {};
  } else if (context === 'all') {
    changedProperties = {};
    additionalChangedProperties = {};
    isComplexObjectMergedInternal = {};
  }
  if (isComplexObjectMergedInternal[context]) {
    isComplexObjectMergedInternal[context] = false;
  }
};

const resetValuesForDependentFields = (context: string, reference: string, visited: Set<string> = new Set()) => {
  if (visited.has(reference)) {
    return;
  }
  visited.add(reference);
  const dependencies: string[] = DatapageDependencyMap.getDependencies(context, reference);
  const resettingDependency: string[] | { mode: string; propertyName: string }[] = CascadeManager.getResetDependencies(
    context,
    reference
  );
  [...dependencies, ...resettingDependency].forEach((dependency) => {
    const propertyNameFullPath = Utils.getPropertyNameFromDependency(dependency);
    if (typeof dependency === 'object' && dependency.mode === 'multiRecord') {
      const dependencyList = propertyNameFullPath.split('.');
      const referencePropName = `.${dependencyList.pop()}`;
      const listPageReference = dependencyList.join('.');
      if (StateMachineManager.isStateMachineExist(context, listPageReference, referencePropName)) {
        const stateMachine = StateMachineManager.getStateMachine(context, listPageReference, referencePropName);
        stateMachine.deleteAll();
      }
    } else {
      if (PageInstructionUtils.isPageInstruction(propertyNameFullPath)) {
        StateMachineManager.executeStateMachine(context, propertyNameFullPath, '');
      }
      dispatch({
        type: actions.SET_PROPERTY,
        payload: {
          reference: propertyNameFullPath,
          value: '',
          context,
          isArrayDeepMerge: true,
          isImplicit: true
        }
      });
    }
    resetValuesForDependentFields(context, propertyNameFullPath, visited);
  });
};

/**
 * fetches the datapage for dependent fields
 * @param payload payload including propertyName, classID, context and pagereference
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
const handleCascadeChange = (payload: any) => {
  const { context, pageReference, value, reference } = payload;
  CascadeManager.triggerCascade(context, reference, value);
  const dependencies = DatapageDependencyMap.getDependencies(context, reference);
  const datapagePayload: { context: string; datapages: { [key: string]: any } } = {
    context,
    datapages: {}
  };

  resetValuesForDependentFields(context, reference);
  dependencies.forEach((dependency: any) => {
    let dependencyPageReference = pageReference;
    if (!dependency.startsWith(pageReference)) {
      dependencyPageReference = '';
    }
    const dependentPropertySplitArray = dependency
      .replace(`${dependencyPageReference}`, '')
      .split('.')
      .filter((val: string) => !!val);
    dependentPropertySplitArray.pop();
    const parentPropertyName = dependentPropertySplitArray.join('.');
    const classID = getValue(`.${parentPropertyName}`, dependencyPageReference, context)?.classID || payload.classID;
    const metadata: any = getPropertyMetadata(dependency.split('.').pop(), classID);
    const { datasource } = metadata;
    const dataViewParameters: { [key: string]: any } = {};
    const hashParts: string[] = [];
    datasource.parameters.forEach((param: any) => {
      if (param?.value) {
        dataViewParameters[param.name] = AnnotationUtils.isProperty(param.value)
          ? getValue(
              `.${Utils.getFullPropertyReference([parentPropertyName, Utils.getPropertyName(param.value)])}`,
              dependencyPageReference,
              context
            )
          : param.value;

        if (
          dataViewParameters[param.name] === undefined ||
          dataViewParameters[param.name] === null ||
          dataViewParameters[param.name] === ''
        ) {
          dataViewParameters[param.name] = '';
          hashParts.push(PegaConstants.NULL_STRING);
        } else {
          hashParts.push(dataViewParameters[param.name]);
        }
      } else {
        dataViewParameters[param.name] = '';
        hashParts.push(PegaConstants.NULL_STRING);
      }
    });
    const hash = datasource.name.concat(
      PegaConstants.UNDERSCORE_STRING,
      Utils.getHashedString(hashParts.join(PegaConstants.HASH_DELIMITER))
    );
    const { name } = datasource;
    const dataPageMetaData = getDataPageMetadata(name);
    datapagePayload.datapages[hash] = {
      mode: datasource.mode,
      propertyName: dependency,
      name: datasource.name,
      dataViewParameters,
      refreshStrategy: dataPageMetaData?.refreshStrategy
    };
  });
  if (dependencies.length) {
    dispatch({
      type: actions.FETCH_DATAPAGE,
      payload: datapagePayload
    });
  }
};

/**
 * Whenever redux triggers action, this api will
 * be called to handle Field-Change action.
 * @param store the store that will be affected
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
const propertyChangeMiddleware = () => (next: any) => (action: any) => {
  const { type, payload = {} } = action || {};

  if (type === actions.FIELD_CHANGE) {
    handleChange(payload);
    handleCascadeChange(payload);
    // setTimeout is used to defer the REFRESH_DATA trigger until after the current call stack completes. This ensures
    // React components have finished re-rendering based on the latest Redux store state. It helps send the correct data
    // in the refresh payload, especially during field visibility transitions (e.g., hidden <-> visible).
    ActionsSequencer.registerBlockingAction(payload.context).catch(() => {});
    setTimeout(() => {
      if (isPropertyDirty(payload.context, payload.reference) || isPropertyDirtyPageInstruction(payload.context)) {
        RefreshHelpers.refreshOnPropertyChange(payload);
      }
      RefreshManager.triggerRefreshForType('PROP_CHANGE', payload.reference, payload.context);
      ActionsSequencer.deRegisterBlockingAction(payload.context).catch(() => {});
    }, 0);
  }

  if (type === actions.LIST_ACTION && !payload?.isDefault) {
    const { context, referenceList, c11nEnv, listIndex, instruction, skipDirtyValidation = false } = payload;
    if (listIndex === 0 && instruction === 'INSERT' && c11nEnv) {
      inspectChildrenForRefreshTargets(c11nEnv);
    }
    RefreshHelpers.refreshOnPropertyChange({ context, pageReference: referenceList });
    if (!skipDirtyValidation) {
      isComplexObjectMergedInternal[`${context}`] = true;
    }
  }

  if (type === actions.SET_PROPERTY || type === actions.REPLACE_PROPERTY) {
    const { reference, isImplicit, context, skipDirtyValidation = false } = payload;
    TreeManager.updateFieldNodeValue(context, reference, payload.value);

    if (!PageInstructionUtils.isPageInstruction(reference) && !skipDirtyValidation) recordChange(action);
    else if (!skipDirtyValidation) {
      isComplexObjectMergedInternal[`${context}`] = true;
    }
    if (payload.removePropertyFromChangedList || isImplicit) deleteRecordedChange(payload);
  }

  if (type === actions.CLEAR_TRANSIENT_DATA || type === actions.PURGE_TRANSIENT_DATA) {
    const { transientItemID: context, data } = payload;
    Utils.iterateLeafNodes(data, (path) => {
      deleteRecordedChange({
        context,
        value: '',
        reference: path.join('.')
      });
    });
  }

  const resetChangesActionArray = [
    actions.UPDATE_CONTAINER_ITEM,
    actions.REMOVE_CONTAINER_ITEM,
    actions.REPLACE_CONTAINER_ITEM,
    actions.REMOVE_ALL_TRANSIENT_ITEMS
  ];

  if (type && resetChangesActionArray.includes(type)) {
    resetChangedProperties(payload.context);
    CascadeManager.purgeResetDependencies(payload.context);
    RefreshHelpers.removeViewsFromTargetList(payload.context);
    const childContexts = payload.childContainersContexts || [];
    childContexts.forEach((childContext: any) => {
      resetChangedProperties(childContext);
    });
    const transientItems = payload.transientItems || [];
    transientItems.forEach((transientItem: any) => {
      resetChangedProperties(transientItem);
    });
  }

  next(action);
};

export default {
  propertyChangeMiddleware,
  getChanges,
  recordChange,
  isDirty,
  isPropertyDirty,
  isContextDirty,
  resetChangedProperties,
  deleteRecordedChange,
  isComplexObjectMerged
};
